import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { removeMember, grantChannelAdmin, revokeChannelAdmin } from '../../store/slices/channelSlice';
import { fetchUsers } from '../../store/slices/userSlice';
import { addNotification } from '../../store/slices/uiSlice';
import axios from 'axios';

import { API_URL } from '../../config/api';
import PresignedAvatar from '../common/PresignedAvatar';
import { getRealmDoc, upsertRealmDocs } from '../../realm';
import { resolveAllUsers } from '../../realm/offlineLookups';

const ChannelMembers = ({ channel, onClose }) => {
  const dispatch = useDispatch();
  const { user: currentUser } = useSelector((state) => state.auth);
  const reduxUsers = useSelector((state) => state.users.users || []);
  const reduxChannel = useSelector((state) => {
    const list = [...(state.channels.channels || []), ...(state.channels.directMessages || [])];
    return list.find((item) => item?.id === channel?.id) || null;
  });
  const [memberDetails, setMemberDetails] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [fullChannel, setFullChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [addingUserId, setAddingUserId] = useState(null);
  const [grantingUserId, setGrantingUserId] = useState(null);
  const [revokingAdminUserId, setRevokingAdminUserId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddMember, setShowAddMember] = useState(false);
  const [activeTab, setActiveTab] = useState('members');

  const channelTypeOf = (ch) => {
    const raw = ch?.type;
    return (
      typeof raw === 'string'
        ? raw
        : (raw && typeof raw === 'object' ? (raw.name || raw.type || '') : String(raw || ''))
    ).toUpperCase();
  };

  const applyChannelUsers = (channelDoc, users) => {
    const memberIds = channelDoc?.memberIds || [];
    const isPublicChannel = !channelDoc?.isPrivate && channelTypeOf(channelDoc) === 'PUBLIC';
    const withStatus = (users || []).map((user) => ({
      ...user,
      isMember: memberIds.includes(user.id),
    }));
    setAllUsers(withStatus);
    setMemberDetails(isPublicChannel ? withStatus : withStatus.filter((user) => user.isMember));
  };

  useEffect(() => {
    const fetchMemberDetails = async () => {
      if (!channel || !channel.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        if (!reduxUsers.length) {
          dispatch(fetchUsers());
        }
        const localChannel = (await getRealmDoc('Channel', channel.id)) || reduxChannel || channel;
        setFullChannel(localChannel);
        const users = await resolveAllUsers(reduxUsers);
        applyChannelUsers(localChannel, users);
      } catch (error) {
        console.error('Error fetching member details:', error);
        dispatch(addNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to load member details',
        }));
      } finally {
        setLoading(false);
      }
    };

    fetchMemberDetails();
  }, [channel, currentUser, dispatch, reduxUsers, reduxChannel]);

  const handleAddMember = async (userId) => {
    setAddingUserId(userId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/channels/${channel.id}/members/${userId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      const updatedChannel = response.data;
      setFullChannel(updatedChannel);
      upsertRealmDocs('Channel', [updatedChannel]);
      
      dispatch(addNotification({
        type: 'success',
        title: 'Success',
        message: 'Member added successfully',
      }));

      const users = await resolveAllUsers(reduxUsers);
      applyChannelUsers(updatedChannel, users);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.message || typeof error === 'string' ? error : 'Failed to add member',
      }));
    } finally {
      setAddingUserId(null);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member from the channel?')) {
      return;
    }

    setRemovingUserId(userId);
    try {
      const result = await dispatch(removeMember({ channelId: channel.id, userId })).unwrap();
      dispatch(addNotification({
        type: 'success',
        title: 'Success',
        message: 'Member removed successfully',
      }));
      const updatedChannel = result.channel
        || (await getRealmDoc('Channel', channel.id))
        || { ...channel, memberIds: (channel.memberIds || []).filter((id) => id !== userId) };
      setFullChannel(updatedChannel);
      const users = await resolveAllUsers(reduxUsers);
      applyChannelUsers(updatedChannel, users);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: typeof error === 'string' ? error : 'Failed to remove member',
      }));
    } finally {
      setRemovingUserId(null);
    }
  };

  const handleGrantAdmin = async (userId) => {
    setGrantingUserId(userId);
    try {
      const result = await dispatch(grantChannelAdmin({ channelId: channel.id, userId })).unwrap();
      setFullChannel(result.channel);
      dispatch(addNotification({
        type: 'success',
        title: 'Success',
        message: 'Admin access granted successfully',
      }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: typeof error === 'string' ? error : 'Failed to grant admin status',
      }));
    } finally {
      setGrantingUserId(null);
    }
  };

  const handleRevokeAdmin = async (userId) => {
    if (!window.confirm('Are you sure you want to remove admin access from this member?')) {
      return;
    }

    setRevokingAdminUserId(userId);
    try {
      const result = await dispatch(revokeChannelAdmin({ channelId: channel.id, userId })).unwrap();
      setFullChannel(result.channel);
      dispatch(addNotification({
        type: 'success',
        title: 'Success',
        message: 'Admin access revoked successfully',
      }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: typeof error === 'string' ? error : 'Failed to revoke admin status',
      }));
    } finally {
      setRevokingAdminUserId(null);
    }
  };

  const channelData = fullChannel || channel;
  const isAdmin = channelData?.adminIds?.includes(currentUser?.id);
  const isCreator = channelData?.createdBy === currentUser?.id;
  const canAddMembers = isAdmin || isCreator;
  const canManageAdmins = isCreator;
  const memberIds = channelData?.memberIds || [];
  const isPublicChannel = !channelData?.isPrivate && channelTypeOf(channelData) === 'PUBLIC';

  // Filter users for add member section
  const filteredUsersForAdd = allUsers.filter(user => {
    if (user.id === currentUser?.id) return false; // Don't show current user
    if (memberIds.includes(user.id)) return false; // Don't show existing members
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      const username = user.username.toLowerCase();
      return fullName.includes(query) || username.includes(query);
    }
    return true;
  });

  if (!channel) return null;

  return (
    <div className="flex flex-col h-full min-h-0 bg-white dark:bg-gray-800">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Channel Details
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex p-1 bg-gray-100 dark:bg-gray-900 rounded-xl">
          <button
            onClick={() => setActiveTab('members')}
            className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-all ${
              activeTab === 'members'
                ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Members
          </button>
          <button
            onClick={() => setActiveTab('files')}
            className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-all ${
              activeTab === 'files'
                ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            Resources
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
            <div className="px-4 py-2 bg-gray-50/50 dark:bg-gray-900/20 border-b border-gray-100 dark:border-gray-700/50">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {memberDetails.filter(m => m.isMember !== false).length} {channelData?.isPrivate ? 'members' : 'users'}
              </p>
              
              {/* Add Member Button */}
              {canAddMembers && (
                <button
                  onClick={() => setShowAddMember(!showAddMember)}
                  className="mt-2 w-full flex items-center justify-center space-x-2 px-3 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors border border-primary-100 dark:border-primary-800/50"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>{showAddMember ? 'Hide' : 'Add'} Members</span>
                </button>
              )}
            </div>

            {/* Add Member Section */}
            {showAddMember && canAddMembers && (
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Search users to add..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                
                {filteredUsersForAdd.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                    {searchQuery ? 'No users found' : 'All users are already members'}
                  </p>
                ) : (
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {filteredUsersForAdd.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          <PresignedAvatar
                            fileKey={user.profilePicture}
                            name={`${user.firstName} ${user.lastName}`}
                            initials={`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`}
                            className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                            fallbackClassName="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                              {user.firstName} {user.lastName}
                            </p>
                            <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                              @{user.username}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddMember(user.id)}
                          disabled={addingUserId === user.id}
                          className="ml-2 px-2 py-1 text-[10px] font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {addingUserId === user.id ? (
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 12 5.373 12 12H4z"></path>
                            </svg>
                          ) : (
                            'Add'
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="p-4 space-y-2">
              {loading ? (
                <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto mb-2"></div>
                  Loading members...
                </div>
              ) : memberDetails.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-8">
                  No members found
                </div>
              ) : (
                <div className="space-y-1">
                  {memberDetails.map((member) => {
                    const isCurrentUser = member.id === currentUser?.id;
                    const isMemberAdmin = channelData?.adminIds?.includes(member.id);
                    const isMemberCreator = channelData?.createdBy === member.id;
                    const isActualMember = member.isMember !== false;
                    const canRemove = isPublicChannel && isActualMember && (isAdmin || isCreator) && !isCurrentUser && !isMemberCreator;
                    const canGrantAdmin = canManageAdmins && isActualMember && !isMemberCreator && !isMemberAdmin;
                    const canRevokeAdmin = canManageAdmins && isActualMember && isMemberAdmin && !isMemberCreator;

                    return (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div className="flex items-center space-x-2.5 flex-1 min-w-0">
                          <PresignedAvatar
                            fileKey={member.profilePicture}
                            name={`${member.firstName} ${member.lastName}`}
                            initials={`${member.firstName?.[0] || ''}${member.lastName?.[0] || ''}`}
                            className="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-gray-100 dark:border-gray-700"
                            fallbackClassName="w-9 h-9 bg-primary-500 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-1.5 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {member.firstName} {member.lastName}
                                {isCurrentUser && <span className="text-gray-400 font-normal ml-1">(You)</span>}
                              </p>
                              {isMemberCreator && (
                                <span className="flex-shrink-0 text-[9px] bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200 px-1.5 py-0.5 rounded font-bold uppercase tracking-tighter">
                                  Owner
                                </span>
                              )}
                              {isMemberAdmin && !isMemberCreator && (
                                <span className="flex-shrink-0 text-[9px] bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 px-1.5 py-0.5 rounded font-bold uppercase tracking-tighter">
                                  Admin
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                              @{member.username}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          {isPublicChannel && !isActualMember && canAddMembers && (
                            <button
                              onClick={() => handleAddMember(member.id)}
                              disabled={addingUserId === member.id}
                              className="px-1.5 py-0.5 text-[10px] font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 transition-colors"
                            >
                              Add
                            </button>
                          )}
                          {canGrantAdmin && (
                            <button
                              onClick={() => handleGrantAdmin(member.id)}
                              disabled={grantingUserId === member.id}
                              className="px-1.5 py-0.5 text-[10px] font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                              title="Make admin"
                            >
                              {grantingUserId === member.id ? (
                                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 12 5.373 12 12H4z"></path>
                                </svg>
                              ) : (
                                'Make admin'
                              )}
                            </button>
                          )}
                          {canRevokeAdmin && (
                            <button
                              onClick={() => handleRevokeAdmin(member.id)}
                              disabled={revokingAdminUserId === member.id}
                              className="px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50 transition-colors border border-blue-200 dark:border-blue-800"
                              title="Remove admin"
                            >
                              {revokingAdminUserId === member.id ? (
                                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 12 5.373 12 12H4z"></path>
                                </svg>
                              ) : (
                                'Remove admin'
                              )}
                            </button>
                          )}
                          {canRemove && (
                            <button
                              onClick={() => handleRemoveMember(member.id)}
                              disabled={removingUserId === member.id}
                              className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                              title="Remove member"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
      </div>
    </div>
  );
};

export default ChannelMembers;
